import { ActionIcon, Avatar, Menu, Text, useComputedColorScheme, useMantineColorScheme } from '@mantine/core';
import { modals } from '@mantine/modals';
import { useLogoutFunction } from '@propelauth/react';
import {
  IconArrowsExchange,
  IconLogout,
  IconMenu2,
  IconMoonFilled,
  IconSunFilled,
  IconUsersGroup,
} from '@tabler/icons-react';
import { useAtom } from 'jotai';
import { navbarOpenAtom, hideNavbarAtom } from '../../atoms/atoms';
import { env } from '~/env.mjs';
import { useAuthData } from '~/hooks/auth/use-auth-data';
import { useProjectId } from '~/hooks/context/useProjectId';
import { useViewModeValue } from '~/hooks/context/useViewMode';
import { useSlack } from '~/hooks/slack/useSlack';
import ChangeTeamForm from '../forms/ChangeTeamForm';
import UniversalSearchButton from '../search/UniversalSearchButton';
import { SlackLogo } from '../slack/SlackLogo';
import DarkModeSwitch from './DarkModeSwitcher';
import { HeaderButtons } from './HeaderButtons';
import ProjectName from './ProjectName';
import { ViewModeSwitcher } from './ViewModeSwitcher';
import { withUrlParams } from '~/server/logic/url/addParams';

export function Header() {
  const { activeOrg, orgHelper, user } = useAuthData();
  const orgs = orgHelper?.getOrgs();

  const signOut = useLogoutFunction();

  const { toggleColorScheme } = useMantineColorScheme();
  const colorScheme = useComputedColorScheme();
  const darkMode = colorScheme === 'dark';

  const projectId = useProjectId();

  const { connectSlack } = useSlack();
  const [, setNavbarOpen] = useAtom(navbarOpenAtom);
  const [hideNavbar, setHideNavbarAtom] = useAtom(hideNavbarAtom);
  const viewMode = useViewModeValue(projectId);

  return (
    <div className="flex h-[60px] items-center justify-between px-4 md:px-10">
      {/* Main header content */}
      <div className="flex items-center space-x-2">
        {hideNavbar ?
          <button
            onClick={() => {
              setHideNavbarAtom(false);
              setNavbarOpen(true);
            }}
          >
            <IconMenu2 color={colorScheme === 'dark' ? '#FCF5E5' : 'black'} />
          </button>
        : null}
        <ProjectName />
      </div>

      {viewMode !== 'agent' && (
        <div className="flex items-center gap-2">
          <UniversalSearchButton />
          <HeaderButtons />
        </div>
      )}

      <div className="flex items-center justify-center space-x-6">
        <ViewModeSwitcher />
        <DarkModeSwitch />

        <Menu
          shadow="md"
          width={200}
          transitionProps={{ transition: 'pop', duration: 200 }}
        >
          <Menu.Target>
            {/* Must wrap in ActionIcon for accessibility reasons */}
            <ActionIcon
              title="Menu"
              size={25}
              variant="transparent"
              className="rounded-full"
            >
              {user?.pictureUrl ?
                <Avatar
                  src={user?.pictureUrl || ''}
                  variant="filled"
                  radius="xl"
                  size={23}
                  alt="Avatar"
                />
              : <Avatar
                  size={23}
                  radius="xl"
                  alt="Avatar"
                  className="capitalize"
                >
                  {user?.username?.charAt(0) || user?.email?.charAt(0)}
                </Avatar>
              }
            </ActionIcon>
          </Menu.Target>

          <Menu.Dropdown>
            <Menu.Label>Account</Menu.Label>
            <div className="w-[80%]">
              <Menu.Label className="truncate">{user?.email}</Menu.Label>
            </div>
            <Menu.Item
              closeMenuOnClick={false}
              leftSection={
                darkMode ?
                  <IconSunFilled
                    className="text-yellow-300"
                    size={14}
                  />
                : <IconMoonFilled size={14} />
              }
              rightSection={
                <Text
                  size="xs"
                  c="dimmed"
                >
                  ⌘+SHIFT+L
                </Text>
              }
              onClick={() => toggleColorScheme()}
            >
              {darkMode ? 'Light' : 'Dark'} Mode
            </Menu.Item>
            <Menu.Item
              leftSection={<IconLogout size={14} />}
              onClick={async () => {
                await signOut(true);
              }}
            >
              Sign Out
            </Menu.Item>
            {projectId ?
              <>
                <Menu.Label>Notification</Menu.Label>
                {/* TODO: eventually allow settings here */}
                {/* <Menu.Item leftSection={<IconSettings size={14} />}>Settings</Menu.Item> */}
                <Menu.Item
                  leftSection={<SlackLogo size={14} />}
                  component="a"
                  target="_blank"
                  onClick={() => {
                    connectSlack(projectId).catch(() => undefined);
                  }}
                >
                  Slack Alerts
                </Menu.Item>
              </>
            : null}
            <Menu.Label>Team{(orgs || []).length > 1 ? ` - ${activeOrg?.orgName}` : ''}</Menu.Label>
            {(orgs || []).length > 1 && (
              <Menu.Item
                leftSection={<IconArrowsExchange size={14} />}
                onClick={() => {
                  modals.open({
                    title: 'Switch Team',
                    children: <ChangeTeamForm />,
                  });
                }}
              >
                Switch Team
              </Menu.Item>
            )}
            {activeOrg?.orgId ?
              <Menu.Item
                leftSection={<IconUsersGroup size={14} />}
                component="a"
                target="_blank"
                href={withUrlParams(`${env.NEXT_PUBLIC_AUTH_URL}/org/members/${activeOrg.orgId}`, {
                  colorScheme,
                })}
              >
                Manage Team
              </Menu.Item>
            : null}
          </Menu.Dropdown>
        </Menu>
      </div>
    </div>
  );
}
